/**
 * PRE-197: canonical display metadata for every LLM `purpose` recorded in the
 * `llm_calls` ledger — the human label + a one-line description of which call it
 * is and where it fires. The admin Costs screen renders these as tooltips.
 *
 * Single source of truth for the copy: `LLM_PURPOSE_META` is a
 * `Record<LlmPurpose, …>`, so adding a purpose to `LLM_PURPOSES` without a
 * description is a COMPILE error. The runtime list mirrors the DB enum
 * (`LLM_PURPOSES` in packages/db) — an api-side guardrail test asserts the two
 * never drift, so a new purpose forces its description here too.
 */
export const LLM_PURPOSES = [
  'tutor',
  'scoring',
  'authoring',
  'roadmap',
  'cv_parse',
  'jd_parse',
  'jd_parse_vision',
  'cv_jd_gap',
  'skill_rate',
] as const;
export type LlmPurpose = (typeof LLM_PURPOSES)[number];

/**
 * PRE-131: the layer above `purpose` — who the spend was for.
 *
 * A purpose is the right grain for "what ran", and the wrong grain for the
 * question a cost screen is actually asked: is this the product or is this us?
 * The live ledger answered that starkly — 92% of thirty days' spend was
 * `rtm_review`, an internal tool, while everything a learner touched cost
 * fractions of a cent. A flat list lets that sit unnoticed because the two
 * biggest numbers are simply adjacent. (PRE-139 has since deleted that lane;
 * the split is what surfaced it, and it still holds for whatever comes next.)
 *
 * So the top split is `learner` vs `internal`, and the phases sit under it.
 * `retired` is its own group: a purpose whose code is gone (`rule_classify`,
 * deleted in PRE-126; `rtm_review`, PRE-139) still has rows in the ledger, and
 * those rows are a fact. There used to be an `internal` group beside it, holding
 * the product-document review; PRE-139 deleted that lane, so the group had no
 * live members and its rows are retired history like any other.
 * Filing them under an active feature would overstate that feature; dropping
 * them would rewrite history. They are shown, and they are shown as over.
 */
export const LLM_PURPOSE_GROUPS = ['onboarding', 'practice', 'plan', 'content', 'retired'] as const;
export type LlmPurposeGroup = (typeof LLM_PURPOSE_GROUPS)[number];

export interface LlmPurposeGroupMeta {
  label: string;
  /**
   * Whether the spend was incurred serving a LEARNER.
   *
   * The one number that scales with users. Content authoring is not learner
   * spend even though learners read the result — it runs once per question, on
   * an admin's click, and does not grow when a thousand people answer it.
   */
  learnerFacing: boolean;
  description: string;
}

export const LLM_PURPOSE_GROUP_META: Record<LlmPurposeGroup, LlmPurposeGroupMeta> = {
  onboarding: {
    label: 'Onboarding',
    learnerFacing: true,
    description:
      'Reading what a learner brings with them — CV, job description, and the skill ratings drawn from them. Runs once per learner, at signup.',
  },
  practice: {
    label: 'Practice & scoring',
    learnerFacing: true,
    description:
      'The loop itself: tutor turns while answering, and the rubric judge that grades the answers. Grows with every session a learner runs.',
  },
  plan: {
    label: 'Planning',
    learnerFacing: true,
    description:
      'Turning what was measured into what to study next — roadmap authoring and CV↔JD gap analysis.',
  },
  content: {
    label: 'Content authoring',
    learnerFacing: false,
    description:
      'Writing the question bank. Paid once per question on an admin action; it does not grow with the number of learners who answer it.',
  },
  retired: {
    label: 'Retired',
    learnerFacing: false,
    description:
      'Purposes whose code no longer exists. The ledger rows are real history and stay counted, but they can never grow again.',
  },
};

export interface LlmPurposeMeta {
  /** Short human label for the purpose. */
  label: string;
  /** One-line description: which LLM call this is and which flow triggers it. */
  description: string;
  /** PRE-131: which group this purpose rolls up into. */
  group: LlmPurposeGroup;
}

export const LLM_PURPOSE_META: Record<LlmPurpose, LlmPurposeMeta> = {
  tutor: {
    label: 'Tutor',
    description:
      'In-app AI tutor turns — the hint/explanation responses a learner gets while practising.',
    group: 'practice',
  },
  scoring: {
    label: 'Scoring',
    description:
      'Answer scoring — the LLM rubric judge that grades free-text answers (blended with the heuristic scorer).',
    group: 'practice',
  },
  authoring: {
    label: 'Authoring',
    description:
      'Question authoring — admin question/rubric generation (Generate screen) and simulated-answer synthesis.',
    group: 'content',
  },
  roadmap: {
    label: 'Roadmap',
    description:
      'Roadmap generation — the LLM-authored 7-day personalized plan built from assessment scores.',
    group: 'plan',
  },
  cv_parse: {
    label: 'CV parse',
    description: "CV parsing — structured extraction of a candidate's CV into skills & experience.",
    group: 'onboarding',
  },
  jd_parse: {
    label: 'JD parse',
    description:
      'Job-description parsing — structured extraction of a JD (text) into role requirements.',
    group: 'onboarding',
  },
  jd_parse_vision: {
    label: 'JD parse (vision)',
    description:
      'Job-description parsing from an image — extracting a JD screenshot via a vision model.',
    group: 'onboarding',
  },
  cv_jd_gap: {
    label: 'CV↔JD gap',
    description:
      'CV ↔ JD gap analysis — comparing a parsed CV against a parsed JD to surface preparation gaps.',
    group: 'plan',
  },
  skill_rate: {
    label: 'Skill rating',
    description:
      'CV skill rating — reads an uploaded CV and scores each skill it names 1-5, as a suggestion the learner then approves or edits.',
    group: 'onboarding',
  },
};

/**
 * Look up a purpose's metadata. Returns null for an unknown value (a purpose in
 * the ledger with no description yet) so callers can surface a visible warning
 * instead of silently rendering a bare code.
 */
export function describeLlmPurpose(purpose: string): LlmPurposeMeta | null {
  return (LLM_PURPOSE_META as Record<string, LlmPurposeMeta>)[purpose] ?? null;
}

/**
 * PRE-131: which group a ledger row rolls up into.
 *
 * A purpose the code no longer has still has rows — `rule_classify` was deleted
 * in PRE-126 and left 41 calls behind — and those rows have to land somewhere.
 * `retired` is where, because the alternatives are both lies: attributing them
 * to an active feature overstates it, and dropping them rewrites what we spent.
 *
 * A purpose that is genuinely unknown (added to the DB enum, never described)
 * also lands here rather than vanishing from the totals. The Costs screen's
 * existing warning badge is what surfaces it as unexplained; this function's
 * job is only to make sure the money is still counted.
 */
export function groupForLlmPurpose(purpose: string): LlmPurposeGroup {
  return describeLlmPurpose(purpose)?.group ?? 'retired';
}
